import { api, apiError } from './client';
import * as adapt from './adapt';
import type {
  Page, RawBooking, RawBusy, RawCustomer, RawDictionaryNode, RawPayment, RawPlan, RawQuote, RawRating, RawResource,
  RawRound, RawSession, RawStay, RawVerification,
} from './raw';
import type { Amenity, CalendarDay, ListingCard, ListingDetail, Me, Paginated, PaymentIntent, PublicReview, Quote, Trip } from '@/types';
import { useSessionStore } from '@/stores/session';

export { monthsBetween } from './adapt';

// ---------- auth / profile (customer contour of the core) ----------
export interface Session {
  access: string;
  refresh: string;
  user: Me;
}

async function session(raw: RawSession): Promise<Session> {
  return { access: raw.access, refresh: raw.refresh, user: adapt.me(raw.customer, null) };
}

export const authTelegram = (initData: string) =>
  api.post<RawSession>('/customer/auth/telegram', { init_data: initData }).then((r) => session(r.data));

// App-store review numbers (the box DEMO_LOGIN_PHONES) sign in with a fixed code over the sms channel only, and the
// box has no sms sender. So sms goes first: a demo number passes without throttling, a regular one gets
// channel_unavailable before the core stores any code, and then asks Telegram (Telegram first would store an
// undeliverable code for a demo number and throttle the next try). The channel that worked checks the code.
let channel: 'telegram' | 'sms' = 'telegram';

const sendCode = (phone: string, via: typeof channel) =>
  api.post<{ sent: boolean; debug_code: string | null }>('/customer/auth/code', { channel: via, destination: phone }).then((r) => {
    channel = via;
    return r.data;
  });

/** The code comes from the box bot to whoever shared this phone with it; otherwise 400 channel_unavailable. */
export const requestCode = (phone: string) =>
  sendCode(phone, 'sms').catch((e) => {
    if (apiError(e, '').code !== 'channel_unavailable') throw e;
    return sendCode(phone, 'telegram');
  });

export const verifyCode = (phone: string, code: string) =>
  api.post<RawSession>('/customer/auth/code/verify', { channel, destination: phone, code }).then((r) => session(r.data));

export const refreshSession = (refresh: string) =>
  api.post<RawSession>('/customer/auth/refresh', { refresh }).then((r) => session(r.data));

export const logout = (refresh: string) => api.post('/customer/auth/logout', { refresh }).catch(() => undefined);

const verification = () => api.get<RawVerification>('/customer/me/verification').then((r) => r.data).catch(() => null);

export const fetchMe = async (): Promise<Me> => {
  const [customer, check] = await Promise.all([api.get<RawCustomer>('/customer/me').then((r) => r.data), verification()]);
  return adapt.me(customer, check);
};

export const updateMe = async (patch: Partial<Pick<Me, 'first_name' | 'last_name' | 'email' | 'language'>>): Promise<Me> => {
  const body = { ...patch, email: patch.email === '' ? null : patch.email };
  const customer = (await api.patch<RawCustomer>('/customer/me', body)).data;
  return adapt.me(customer, await verification());
};

/** Stripe Identity: the hosted page link (`url`) — opened in the browser. */
export const startIdentity = (returnUrl: string) =>
  api.post<RawVerification>('/customer/me/verification', { return_url: returnUrl }).then((r) => r.data.url);

// ---------- catalogue (storefront of the booking module) ----------
export interface SearchParams {
  q?: string;
  city?: string;
  check_in?: string;
  check_out?: string;
  guests?: number;
  rental_type?: 'short' | 'long';
  price_max?: number;
  property_type?: string;
  bedrooms?: number;
  bbox?: string;
  sort?: string;
  page?: number;
  page_size?: number;
}

// old sort names → storefront ones (price / -price / new / rating); "recommended" — the default order
const SORTS: Record<string, string> = { price_asc: 'price', price_desc: '-price', newest: 'new', rating: 'rating' };

let favoriteIds: Set<string> | null = null;
/** Favourites of the signed-in guest — to mark cards; reset on login / logout. */
export function forgetFavorites() {
  favoriteIds = null;
}
async function favorites(): Promise<Set<string>> {
  if (favoriteIds) return favoriteIds;
  // without a session — no request at all: a 401 here would only start a needless token refresh
  if (!useSessionStore().token) return new Set();
  try {
    const page = await api.get<Page<{ object_id: string }>>('/customer/favorites', { params: { entity: 'booking.resource', page_size: 100 } });
    favoriteIds = new Set(page.data.results.map((f) => f.object_id));
  } catch {
    return new Set(); // a guest without a session has no favourites
  }
  return favoriteIds;
}

function query(params: SearchParams) {
  const q = new URLSearchParams();
  if (params.q) q.set('q', params.q);
  if (params.city) q.set('attr.city', params.city);
  if (params.check_in && params.check_out) {
    q.set('check_in', params.check_in);
    q.set('check_out', params.check_out);
  }
  if (params.guests) q.set('guests', String(params.guests));
  if (params.price_max) q.set('max_price', String(params.price_max));
  if (params.property_type) q.set('attr.property_type', params.property_type);
  // «from N bedrooms», like the old filter (core ≥ 3.7.6: __gte for numeric fields)
  if (params.bedrooms) q.set('attr.bedrooms__gte', String(params.bedrooms));
  // nightly or monthly — the storefront filters itself (core ≥ 3.7.6), the count is right on every page
  q.set('rent', params.rental_type === 'long' ? 'monthly' : 'nightly');
  if (params.bbox) q.set('bbox', params.bbox);
  if (params.sort && SORTS[params.sort]) q.set('sort', SORTS[params.sort]);
  q.set('page', String(params.page ?? 1));
  if (params.page_size) q.set('page_size', String(params.page_size));
  return q;
}

export const searchListings = async (params: SearchParams): Promise<Paginated<ListingCard>> => {
  const [page, liked] = await Promise.all([
    api.get<Page<RawResource>>('/public/storefront/booking/resources', { params: query(params) }).then((r) => r.data),
    favorites(),
  ]);
  return { count: page.count, next: page.page_next ? String(page.page_next) : null, results: page.results.map((r) => adapt.card(r, liked.has(r.id))) };
};

const resourceCache = new Map<string, Promise<RawResource>>();
function resource(id: string): Promise<RawResource> {
  if (!resourceCache.has(id)) {
    const request = api.get<RawResource>(`/public/storefront/booking/resources/${id}`).then((r) => r.data);
    request.catch(() => resourceCache.delete(id));
    resourceCache.set(id, request);
  }
  return resourceCache.get(id)!;
}

let amenityCache: Promise<Map<string, Amenity>> | null = null;
function amenityIndex() {
  amenityCache ??= dictionary('emcity_amenities').then(adapt.amenityIndex).catch(() => {
    amenityCache = null;
    return new Map<string, Amenity>();
  });
  return amenityCache;
}

export const dictionary = (alias: string) =>
  api.get<{ items: RawDictionaryNode[] }>(`/public/storefront/dictionaries/${alias}`).then((r) => r.data.items);

const rating = (id: string) =>
  api.get<RawRating>(`/public/storefront/reviews/booking.resource/${id}`).then((r) => r.data).catch(() => null);

export const fetchListing = async (id: string): Promise<ListingDetail> => {
  const [raw, liked, amenities, stars] = await Promise.all([resource(id), favorites(), amenityIndex(), rating(id)]);
  return adapt.detail(raw, liked.has(raw.id), amenities, stars);
};

export const fetchCalendar = async (id: string, from: string, days = 365): Promise<{ currency: string; days: CalendarDay[] }> => {
  const [raw, busy] = await Promise.all([
    resource(id),
    api.get<RawBusy[]>(`/public/storefront/booking/resources/${id}/busy`, { params: { start: from, days } }).then((r) => r.data),
  ]);
  return { currency: raw.currency, days: adapt.calendar(raw, busy, from, days) };
};

/** Nightly — check_in / check_out; monthly (rental_type long) — whole months from check_in. */
export const fetchQuote = (id: string, body: { check_in: string; check_out: string; guests: number; rental_type?: string | null }) => {
  const params = body.rental_type === 'long'
    ? { check_in: body.check_in, months: adapt.monthsBetween(body.check_in, body.check_out), guests: body.guests }
    : { check_in: body.check_in, check_out: body.check_out, guests: body.guests };
  return api.get<RawQuote>(`/public/storefront/booking/resources/${id}/quote`, { params }).then((r) => adapt.quote(r.data, body.check_in) as Quote);
};

export const fetchReviews = async (id: string): Promise<{ results: PublicReview[] }> => {
  const stars = await rating(id);
  return { results: stars ? adapt.reviews(stars) : [] };
};

/** Cities with listings — from the storefront itself (the box has no separate destinations list). */
export const fetchDestinations = async () => {
  const page = await api.get<Page<RawResource>>('/public/storefront/booking/resources', { params: { page_size: 100 } }).then((r) => r.data);
  const counts = new Map<string, { country: string; city: string; listings: number }>();
  for (const r of page.results) {
    const city = String(r.attributes.city ?? '');
    if (!city) continue;
    const entry = counts.get(city) ?? { country: String(r.attributes.country ?? ''), city, listings: 0 };
    entry.listings += 1;
    counts.set(city, entry);
  }
  return [...counts.values()].sort((a, b) => b.listings - a.listings);
};

/** Currency of the platform's listings — for the price filter. */
export const fetchCurrency = async () => {
  const page = await api.get<Page<RawResource>>('/public/storefront/booking/resources', { params: { page_size: 1 } }).then((r) => r.data);
  return page.results[0]?.currency ?? 'USD';
};

// ---------- favorites (core favourites of the customer) ----------
export const fetchFavorites = async (): Promise<ListingCard[]> => {
  forgetFavorites();
  const ids = [...(await favorites())];
  const found = await Promise.all(ids.map((id) => resource(id).catch(() => null)));
  return found.filter((r): r is RawResource => !!r).map((r) => adapt.card(r, true));
};
export const addFavorite = (listing: string) =>
  api.put(`/customer/favorites/booking.resource/${listing}`).then(() => favoriteIds?.add(listing));
export const removeFavorite = (listing: string) =>
  api.delete(`/customer/favorites/booking.resource/${listing}`).then(() => favoriteIds?.delete(listing));

// ---------- trips (bookings of the customer + emcity stays) ----------
export interface TripCreate {
  listing: string;
  check_in: string;
  check_out: string;
  guests: number;
  rental_type?: string | null;
  message_to_host?: string;
}

function intent(raw: RawPayment | null, fallbackAmount: number, currency: string): PaymentIntent | null {
  if (!raw?.client_data?.client_secret) return null;
  return {
    id: raw.id ?? '',
    kind: raw.provider,
    amount: raw.amount ? adapt.minorOf(raw.amount, currency) : fallbackAmount,
    currency: raw.currency ?? currency,
    status: raw.status ?? 'pending',
    client_secret: raw.client_data.client_secret,
    publishable_key: raw.client_data.publishable_key ?? '',
  };
}

export const createTrip = async (body: TripCreate): Promise<{ booking: Trip; payment: PaymentIntent | null }> => {
  const monthly = body.rental_type === 'long';
  const payload = {
    resource_id: body.listing,
    check_in: body.check_in,
    ...(monthly ? { months: adapt.monthsBetween(body.check_in, body.check_out) } : { check_out: body.check_out }),
    guests: body.guests,
    note: body.message_to_host ?? '',
  };
  const data = (await api.post<{ booking: RawBooking; payment: RawPayment | null }>('/customer/booking/bookings', payload)).data;
  const raw = await resource(body.listing).catch(() => null);
  const booking = adapt.trip(data.booking, raw, null, undefined, undefined);
  const payment = intent(data.payment, booking.online_amount, booking.currency);
  return { booking, payment };
};

const bookings = () => api.get<RawBooking[]>('/customer/booking/bookings').then((r) => r.data);
const stays = () => api.get<RawStay[]>('/customer/emcity/stays').then((r) => r.data).catch(() => [] as RawStay[]);
const rounds = () => api.get<RawRound[]>('/customer/reviews/rounds').then((r) => r.data).catch(() => [] as RawRound[]);
const plans = (bookingId: string) =>
  api.get<RawPlan[]>('/customer/payments/plans', { params: { entity: 'booking.booking', object_id: bookingId } }).then((r) => r.data).catch(() => [] as RawPlan[]);

function scopeOf(b: RawBooking, today: string): 'upcoming' | 'past' | 'cancelled' {
  if (b.status === 'cancelled' || b.status === 'expired') return 'cancelled';
  if (b.status === 'completed' || b.check_out < today) return 'past';
  return 'upcoming';
}

export const fetchTrips = async (scope: 'upcoming' | 'past' | 'cancelled'): Promise<Paginated<Trip>> => {
  const [all, staysAll] = await Promise.all([bookings(), stays()]);
  const today = new Date().toISOString().slice(0, 10);
  const mine = all.filter((b) => scopeOf(b, today) === scope);
  mine.sort((a, b) => (scope === 'upcoming' ? a.check_in.localeCompare(b.check_in) : b.check_in.localeCompare(a.check_in)));
  const byBooking = new Map(staysAll.map((s) => [s.booking_id, s]));
  const results = await Promise.all(
    mine.map(async (b) => adapt.trip(b, await resource(b.resource_id).catch(() => null), byBooking.get(b.id) ?? null, undefined, undefined)),
  );
  return { count: results.length, next: null, results };
};

export const fetchTrip = async (id: string): Promise<Trip> => {
  const [b, stay, plan, round] = await Promise.all([
    api.get<RawBooking>(`/customer/booking/bookings/${id}`).then((r) => r.data),
    api.get<RawStay>(`/customer/emcity/stays/${id}`).then((r) => r.data).catch(() => null),
    plans(id),
    rounds(),
  ]);
  const raw = await resource(b.resource_id).catch(() => null);
  return adapt.trip(b, raw, stay, plan[0], round.find((r) => r.object_id === id));
};

/** Resume paying an unpaid booking: the box hands out its current pending payment (409 nothing_to_pay — paid or
 *  expired). Works after the app was closed — nothing is kept on the device. */
export const payTrip = async (id: string): Promise<PaymentIntent> => {
  const data = (await api.post<{ booking: RawBooking; payment: RawPayment | null }>(`/customer/booking/bookings/${id}/pay`)).data;
  const value = intent(data.payment, adapt.minorOf(data.booking.total, data.booking.currency), data.booking.currency);
  if (!value) throw Object.assign(new Error('no payment'), { response: { data: { code: 'payment_unavailable', detail: 'Payment is not available.' } } });
  return value;
};

/** A monthly installment that failed (or is due): a new payment with the card entered now. */
export const payInstallment = async (_tripId: string, installmentId: string): Promise<PaymentIntent> => {
  const raw = (await api.post<RawPayment>(`/customer/payments/installments/${installmentId}/pay`)).data;
  const value = intent(raw, 0, raw.currency ?? 'USD');
  if (!value) throw Object.assign(new Error('no payment'), { response: { data: { code: 'payment_unavailable', detail: 'Payment is not available.' } } });
  return value;
};

export const cancellationPreview = async (id: string) => {
  const r = (await api.get<{ percent: number; rule: string; refund: string; deposit: string; currency: string }>(`/customer/booking/bookings/${id}/cancel`)).data;
  return { currency: r.currency, refund_percent: r.percent, guest_refund: adapt.minorOf(r.refund, r.currency), rule: r.rule };
};

export const cancelTrip = async (id: string, reason: string): Promise<Trip> => {
  await api.post(`/customer/booking/bookings/${id}/cancel`, { reason });
  return fetchTrip(id);
};

export const fetchGuide = async (id: string) =>
  adapt.guide((await api.get<RawStay>(`/customer/emcity/stays/${id}`)).data);

/** Review of the stay: the round of the booking (opens after check-out) — rating, criteria, text, note to the host. */
export const reviewTrip = async (id: string, body: Record<string, unknown>) => {
  const round = (await rounds()).find((r) => r.object_id === id);
  if (!round) throw Object.assign(new Error('no round'), { response: { data: { code: 'review_closed', detail: 'Reviews are not open for this stay.' } } });
  const criteria = ['cleanliness', 'accuracy', 'checkin', 'communication', 'location', 'value'];
  const scores = Object.fromEntries(criteria.filter((c) => body[c]).map((c) => [c, Number(body[c])]));
  return api
    .post(`/customer/reviews/rounds/${round.id}/review`, {
      rating: Number(body.rating),
      text: String(body.text ?? ''),
      scores,
      private_note: String(body.private_note ?? ''),
    })
    .then((r) => r.data);
};

// ---------- support (core support module) ----------
export const createTicket = (body: { topic: string; subject: string; text: string; booking?: string }) =>
  api
    .post('/customer/support/tickets', {
      subject: body.subject,
      text: body.text,
      category: body.topic,
      ...(body.booking ? { entity: 'booking.booking', object_id: body.booking } : {}),
    })
    .then((r) => r.data);

/** Topics with labels in the request language (dictionary support_categories, open to guests). */
export const fetchTopics = async () => (await dictionary('support_categories')).map((n) => ({ code: n.code, label: n.label }));

// ---------- devices (push) ----------
export const registerDevice = (platform: 'ios' | 'android' | 'web', token: string) =>
  api.post('/customer/me/devices', { platform, token });
export const unregisterDevice = (token: string) => api.delete('/customer/me/devices', { params: { token } });
